import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * hire 관리 화면 경로만 허용한다 — `/admin` 아래만. 외부 주소 · `//host` · 역슬래시 ·
 * 지원자 화면 · 로그인 화면이면 버리고 `/admin` 으로.
 */
export function safeNext(next: string | null): string {
  if (!next || next.includes('\\') || !/^\/admin(\/|\?|#|$)/.test(next)) return '/admin';
  if (/^\/admin\/login(\/|\?|#|$)/.test(next)) return '/admin';
  return next;
}

interface Props {
  onLogin: () => Promise<void>;
}

/**
 * admin 바로 이동 받기(handbook #16) — `/sso?code=…&next=…`. **직원(관리) 쪽만.**
 * 표는 1분 · 한 번만이라, 받자마자 주소에서 지우고 백엔드에 확인을 맡긴다.
 * 실패하면 관리자 로그인 화면에 「admin 에서 다시 이동해 주세요」. 직접 로그인은 그대로다.
 * adminApi(axios)는 401 이면 로그인 화면으로 바로 보내 안내가 사라지므로 fetch 를 쓴다.
 */
const SsoPage: React.FC<Props> = ({ onLogin }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // 개발 모드에서 두 번 돌아 표를 두 번 쓰지 않게
    started.current = true;
    const code = params.get('code');
    const next = safeNext(params.get('next'));
    window.history.replaceState(null, '', '/sso');
    const fail = (message: string) => navigate('/admin/login', { replace: true, state: { message } });
    if (!code) { fail('바로 이동 정보가 없습니다. admin 에서 다시 이동해 주세요'); return; }

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/admin/auth/sso`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.accessToken) throw new Error('바로 이동이 확인되지 않았습니다. admin 에서 다시 이동해 주세요');
        localStorage.setItem('hire_admin_token', data.accessToken);
        if (data.employee?.email) localStorage.setItem('hire_admin_email', data.employee.email);
        await onLogin();
        navigate(next, { replace: true });
      } catch (e) {
        fail((e as Error).message);
      }
    })();
  }, [params, navigate, onLogin]);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <p className="text-sm text-slate-500">admin 에서 이동하는 중…</p>
    </div>
  );
};

export default SsoPage;
